import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { organizationService, managerService } from '../services';
import {
  Building2,
  Users,
  HeartHandshake,
  UserCog,
  AlertCircle,
} from 'lucide-react';

/**
 * Dashboard Home Page
 * Shows overview statistics and recent data
 */
const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    organizations: 0,
    managers: 0,
    serviceUsers: 0,
    staff: 0,
    loading: true,
  });
  const [organizations, setOrganizations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgsResponse, managersResponse, statsResponse] = await Promise.all([
          organizationService.getAllOrganizations(),
          managerService.getAllManagers(),
          organizationService.getDashboardStats(),
        ]);

        if (orgsResponse.success) {
          setOrganizations(orgsResponse.data || []);
          setStats((prev) => ({
            ...prev,
            organizations: orgsResponse.data?.length || 0,
          }));
        }

        if (managersResponse.success) {
          setManagers(managersResponse.data || []);
          setStats((prev) => ({
            ...prev,
            managers: managersResponse.data?.length || 0,
          }));
        }

        if (statsResponse.success) {
          setStats((prev) => ({
            ...prev,
            // System-wide totals are authoritative (the org list above is
            // paginated, so its length isn't the real org total).
            organizations: statsResponse.data.totalOrganizations ?? prev.organizations,
            serviceUsers: statsResponse.data.totalServiceUsers || 0,
            staff: statsResponse.data.totalStaff || 0,
          }));
        }

        setStats((prev) => ({ ...prev, loading: false }));
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load dashboard data');
        setStats((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchData();
  }, []);

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className={`bg-white dark:bg-[#262b33] rounded-lg shadow p-6 border-l-4 ${color} border-opacity-100 dark:border-opacity-80`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">{value}</p>
        </div>
        <Icon className={`w-12 h-12 opacity-20 dark:opacity-30`} />
      </div>
    </div>
  );


  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 text-white">
        <h1 className="text-3xl font-bold">Welcome back, {user?.name}! 👋</h1>
        <p className="text-blue-100 mt-2">
          Here's what's happening with your organization today.
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Building2}
          label="Total Organizations"
          value={stats.organizations}
          color="border-blue-500"
        />
        <StatCard
          icon={Users}
          label="Total Managers"
          value={stats.managers}
          color="border-green-500"
        />
        <StatCard
          icon={HeartHandshake}
          label="Service Users"
          value={stats.serviceUsers}
          color="border-purple-500"
        />
        <StatCard
          icon={UserCog}
          label="Total Staff"
          value={stats.staff}
          color="border-orange-500"
        />
      </div>


      {/* Recent Organizations */}
      <div className="bg-white dark:bg-[#262b33] rounded-lg shadow overflow-hidden border border-transparent dark:border-gray-800">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Recent Organizations</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-[#31373f]">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">

              {organizations.slice(0, 5).map((org) => (
                <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-[#31373f] transition">
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">{org.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                      {org.organization_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{org.email}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                      Active
                    </span>
                  </td>
                </tr>
              ))}
              {organizations.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    No organizations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* Recent Managers */}
      <div className="bg-white dark:bg-[#262b33] rounded-lg shadow overflow-hidden border border-transparent dark:border-gray-800 mt-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Recent Managers</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-[#31373f]">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">

              {managers.slice(0, 5).map((manager) => (
                <tr key={manager.id} className="hover:bg-gray-50 dark:hover:bg-[#31373f] transition">
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">{manager.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{manager.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {manager.Organization?.name}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        manager.approval_status === 'approved'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                      }`}
                    >
                      {manager.approval_status}
                    </span>
                  </td>
                </tr>
              ))}
              {managers.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    No managers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
